'use client';

import { Fragment, useEffect } from 'react';
import styled from '@emotion/styled';
import { ContentWrap, Css, TableRole } from '@/ui';
import { IJobEntry } from '../../../../contentful/types/IJob';
import { JobListRow } from './JobListRow';
import { JobListHeader } from './JobListHeader';
import ErrorMessage from '../../../shared/components/error/ErrorMessage';
import { ErrorMessages } from '../../../../utils/errorMessages';
import { useScrollIntoView } from '../../../../hooks/useScrollIntoView';
import { useJobsSearch } from '../../hooks/useJobsSearch';
import { JobSearchBar } from './JobSearchBar';
import { JobsPagination } from './JobsPagination';

const StyledContentWrap = styled(ContentWrap)`
	padding-bottom: ${Css.pxToRem(30)};
`;

interface IJobListProps {
	jobs: IJobEntry[];
}

const JOBS_PER_PAGE = 10;

export function JobList({ jobs }: IJobListProps) {
	const search = useJobsSearch(jobs);
	const { ref, scrollIntoView } = useScrollIntoView();

	useEffect(() => {
		if (search.state.pageIndex > 0) {
			scrollIntoView();
		}
	}, [search.state]);

	if (jobs.length === 0) {
		return <ErrorMessage message={ErrorMessages.JobsNotFound} />;
	}

	const items = search.results || jobs;
	const start = search.state.pageIndex * JOBS_PER_PAGE;
	const end = start + JOBS_PER_PAGE;
	const totalPages = Math.ceil(items.length / JOBS_PER_PAGE);
	const pageItems = items.slice(start, end);

	return (
		<StyledContentWrap ref={ref}>
			<JobSearchBar jobs={jobs} state={search.state} onSubmit={search.submit} />
			{items.length === 0 ? (
				<ErrorMessage
					title="No jobs found"
					message={
						search.isActive
							? ErrorMessages.JobsNoSearchResults
							: ErrorMessages.JobsNotFound
					}
					showReloadButton={!search.isActive}
				/>
			) : (
				<>
					<TableRole>
						<JobListHeader />
						{pageItems.map((job) => (
							<Fragment key={job.id}>
								<JobListRow job={job} />
							</Fragment>
						))}
					</TableRole>
					<JobsPagination
						currentPageIndex={search.state.pageIndex}
						totalPages={totalPages}
						getHref={(index) => search.getSubmitHref({ pageIndex: index + 1 })}
					/>
				</>
			)}
		</StyledContentWrap>
	);
}
